// Constraint-listing index entries — Slice 21 dispatch arms for indexBuilder.
//
// Splits the per-listing default + check constraint dispatch out of
// `indexBuilder.ts` so each file stays under the 200-line budget. The two
// exports here are called from `buildIndexForConnection`'s switch.
//
// Default-constraint bodies are bare expressions; the walker emits routine
// refs for function calls + known parameterless built-ins. Check-constraint
// bodies are bare expressions on every engine EXCEPT SQLite, which ships
// the full CREATE TABLE source under the synthetic `check-constraint` kind
// (constraints live inline). For SQLite we run a CREATE-TABLE-aware finder
// that splits the source into individual `CHECK (...)` / `DEFAULT <expr>`
// regions and forwards each region to the same expression walker.

import { buildLineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';

import type { IdentifierIndex } from '../types';

import { walkConstraintExpression } from './constraintExpressionScanner';
import type { PersistedObjectListing } from './objectBodyFetcher';
import { scanSqliteCreateTableConstraints } from './sqliteConstraintFinder';

export function indexDefaultConstraintListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const parseOptions = makeParseOptions(listing.parseOptions.engine);
  walkConstraintExpression(listing.bodyText, 0, parseOptions, listing, 'default', lineIdx, index);
}

export function indexCheckConstraintListing(
  listing: PersistedObjectListing,
  index: IdentifierIndex,
): void {
  const lineIdx = buildLineIndex(listing.bodyText);
  const engine = listing.parseOptions.engine;
  const parseOptions = makeParseOptions(engine);
  if (engine === 'sqlite' && /^\s*CREATE\s+TABLE\b/i.test(listing.bodyText)) {
    scanSqliteCreateTableConstraints(listing.bodyText, parseOptions, listing, lineIdx, index);
    return;
  }
  walkConstraintExpression(listing.bodyText, 0, parseOptions, listing, 'check', lineIdx, index);
}

function makeParseOptions(engine: SqlEngine): ParseOptions {
  return { engine, identifierFolding: defaultFoldingForEngine(engine) };
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
